import { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@/lib/zodcompat'
import { toast } from 'sonner'
import { Dialog } from '@base-ui/react/dialog'
import { obligationSchema, type ObligationFormData } from '@/validators/financial-obligation'
import { useCreateObligation, useUpdateObligation } from '@/hooks/useFinancialObligations'
import { useCashCategories } from '@/hooks/useCashCategories'
import { MoneyInput } from '@/components/form/money-input'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/ui/loading-button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import type { FinancialObligation, FinancialObligationType } from '@/types/financial-obligation'

const selectClass = cn(
  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
)

function apiError(err: unknown, fallback: string) {
  return (
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? fallback
  )
}

function todayISO() {
  return new Date().toISOString().slice(0, 10)
}

interface Props {
  open: boolean
  onClose: () => void
  obligation?: FinancialObligation | null
  defaultType: FinancialObligationType
}

export function ObligationFormModal({ open, onClose, obligation, defaultType }: Props) {
  const isEdit = !!obligation
  const createMutation = useCreateObligation()
  const updateMutation = useUpdateObligation(obligation?.id ?? '')
  const pending = createMutation.isPending || updateMutation.isPending

  const type = obligation?.type ?? defaultType
  const requiredKind = type === 'RECEIVABLE' ? 'INFLOW' : 'OUTFLOW'
  const { data: categoriesData } = useCashCategories({
    kind: requiredKind,
    active: true,
    perPage: 100,
  })
  const categories = categoriesData?.data ?? []

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<ObligationFormData>({
    resolver: zodResolver(obligationSchema),
    defaultValues: {
      type,
      categoryId: '',
      description: '',
      amount: '' as unknown as number,
      dueDate: todayISO(),
      counterparty: '',
      documentNumber: '',
      notes: '',
    },
  })

  useEffect(() => {
    if (open) {
      reset({
        type,
        categoryId: obligation?.categoryId ?? '',
        description: obligation?.description ?? '',
        amount: obligation ? Number.parseFloat(obligation.amount) : ('' as unknown as number),
        dueDate: obligation ? obligation.dueDate.slice(0, 10) : todayISO(),
        counterparty: obligation?.counterparty ?? '',
        documentNumber: obligation?.documentNumber ?? '',
        notes: obligation?.notes ?? '',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, obligation?.id])

  async function onSubmit(data: ObligationFormData) {
    try {
      if (isEdit && obligation) {
        await updateMutation.mutateAsync({
          categoryId: data.categoryId,
          description: data.description,
          amount: data.amount,
          dueDate: new Date(data.dueDate).toISOString(),
          counterparty: data.counterparty || null,
          documentNumber: data.documentNumber || null,
          notes: data.notes || null,
        })
        toast.success('Obrigação atualizada')
      } else {
        await createMutation.mutateAsync({
          ...data,
          dueDate: new Date(data.dueDate).toISOString(),
          counterparty: data.counterparty || null,
          documentNumber: data.documentNumber || null,
          notes: data.notes || null,
        })
        toast.success(type === 'PAYABLE' ? 'Conta a pagar registrada' : 'Conta a receber registrada')
      }
      onClose()
    } catch (err) {
      toast.error(apiError(err, 'Erro ao salvar'))
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={(v) => !v && onClose()}>
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-40 bg-black/50" />
        <Dialog.Popup className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-full max-w-xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl border border-border bg-background p-6 shadow-md">
          <Dialog.Title className="text-lg font-semibold text-foreground">
            {isEdit ? `Editar O-${obligation?.code}` : type === 'PAYABLE' ? 'Nova conta a pagar' : 'Nova conta a receber'}
          </Dialog.Title>
          <Dialog.Description className="text-sm text-muted-foreground">
            {type === 'PAYABLE'
              ? 'Lance um compromisso futuro de pagamento; quitação posta a saída no caixa.'
              : 'Lance um recebível futuro; quitação posta a entrada no caixa.'}
          </Dialog.Description>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="ob-category">Categoria *</Label>
                <Controller
                  name="categoryId"
                  control={control}
                  render={({ field }) => (
                    <select id="ob-category" value={field.value} onChange={field.onChange} className={selectClass}>
                      <option value="" disabled>Selecione…</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  )}
                />
                {categories.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    Cadastre uma categoria de {requiredKind === 'INFLOW' ? 'receita' : 'despesa'} antes.
                  </p>
                )}
                {errors.categoryId && (
                  <p className="text-xs text-destructive">{errors.categoryId.message}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="ob-due">Vencimento *</Label>
                <Input id="ob-due" type="date" aria-invalid={!!errors.dueDate} {...register('dueDate')} />
                {errors.dueDate && (
                  <p className="text-xs text-destructive">{errors.dueDate.message}</p>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ob-desc">Descrição *</Label>
              <Input
                id="ob-desc"
                placeholder={type === 'PAYABLE' ? 'Ex.: Aluguel do escritório' : 'Ex.: Fatura empresa X'}
                aria-invalid={!!errors.description}
                {...register('description')}
              />
              {errors.description && (
                <p className="text-xs text-destructive">{errors.description.message}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="ob-amount">Valor *</Label>
                <MoneyInput id="ob-amount" error={!!errors.amount} {...register('amount')} />
                {errors.amount && <p className="text-xs text-destructive">{errors.amount.message}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ob-cp">{type === 'PAYABLE' ? 'Fornecedor' : 'Cliente'}</Label>
                <Input id="ob-cp" {...register('counterparty')} />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="ob-doc">Nº documento</Label>
                <Input id="ob-doc" placeholder="NF, boleto, contrato" {...register('documentNumber')} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ob-notes">Observações</Label>
                <Input id="ob-notes" {...register('notes')} />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={pending}>
                Cancelar
              </Button>
              <LoadingButton type="submit" loading={pending} loadingText="Salvando…">
                {isEdit ? 'Salvar alterações' : 'Registrar'}
              </LoadingButton>
            </div>
          </form>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
